import React, { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useQueryClient } from "react-query";
import { Modal } from "../shared-components/Modal";
import { NewsItem } from "./docs/News";
import { LegalCaseItem } from "./docs/LegalCase";
import { DiligenceItem } from "./docs/Diligence";
import {
  useCloneReportDocuments,
  useGetItemDocuments,
} from "../api/360FilesAPI";
import { PaperClipIcon } from "@heroicons/react/24/outline";

interface CopyFromReportModalProps {
  open: boolean;
  isSummaryStep: boolean;
  showNewsSection: boolean;
  showLegalSection: boolean;
  showDiligenceSection: boolean;
  sourceReportUuid: string;
  sourceNewsItems: NewsItem[];
  sourceLegalItems: LegalCaseItem[];
  sourceDiligenceItems: DiligenceItem[];
  selectedNewsItems: NewsItem[];
  selectedLegalItems: LegalCaseItem[];
  selectedDiligenceItems: DiligenceItem[];
  selectedNewsIds: string[];
  selectedLegalIds: string[];
  selectedDiligenceIds: string[];
  loadCopySourceError: string;
  isLoadingCopySource: boolean;
  isCopyingItems: boolean;
  destinationReportUuid: string;
  onClose: () => void;
  onSourceReportUuidChange: (value: string) => void;
  onLoadSource: () => void;
  onToggleNews: (externalId: string) => void;
  onToggleLegal: (externalId: string) => void;
  onToggleDiligence: (externalId: string) => void;
  onSelectAllNews: () => void;
  onUnselectAllNews: () => void;
  onSelectAllLegal: () => void;
  onUnselectAllLegal: () => void;
  onSelectAllDiligence: () => void;
  onUnselectAllDiligence: () => void;
  onProceedToSummary: () => void;
  onBackToSelection: () => void;
  onClearInformation: () => void;
  onCopyInProgressChange: (inProgress: boolean) => void;
  onCopySuccess: () => void;
  onCopyError: (message: string) => void;
}

interface ItemFile {
  filename: string;
  url: string;
}

const formatAuthor = (author?: string) => author || "Unknown author";

const formatDateRange = (startDate?: string, endDate?: string | null) =>
  `${startDate || "No start date"}${endDate ? ` - ${endDate}` : ""}`;

const ItemAttachments: React.FC<{
  externalId: string;
  fetchEnabled: boolean;
}> = ({ externalId, fetchEnabled }) => {
  const { data: uploadedFiles = [] } = useGetItemDocuments(externalId, true, {
    enabled: fetchEnabled,
  });

  if (!uploadedFiles.length) {
    return null;
  }

  const openFile = (
    event:
      | React.MouseEvent<HTMLButtonElement>
      | React.KeyboardEvent<HTMLButtonElement>,
    url: string
  ) => {
    event.preventDefault();
    event.stopPropagation();
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      {uploadedFiles.map((file: ItemFile) => (
        <button
          type="button"
          key={`${file.filename}-${file.url}`}
          className="flex flex-row items-center gap-1.5 py-0.5 px-0.5 cursor-pointer"
          onClick={(event) => openFile(event, file.url)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              openFile(event, file.url);
            }
          }}
          aria-label={`Open attachment ${file.filename}`}
        >
          <span className="w-3.5 h-3.5 flex-shrink-0">
            <PaperClipIcon className="w-3.5 h-3.5" />
          </span>
          <p className="text-gray-500 text-[11px] leading-4 truncate">
            {file.filename}
          </p>
        </button>
      ))}
    </>
  );
};

const BaseItemDetails: React.FC<{
  title: string;
  externalId: string;
  children: React.ReactNode;
}> = ({ title, externalId, children }) => {
  const { ref, inView } = useInView({
    rootMargin: "120px",
    triggerOnce: true,
  });

  return (
    <div ref={ref} className="flex-grow min-w-0">
      <h5 className="text-gray-700 font-medium text-xs leading-4 truncate">
        {title}
      </h5>
      <ItemAttachments externalId={externalId} fetchEnabled={inView} />
      <div className="mt-0.5 space-y-0.5">{children}</div>
    </div>
  );
};

const NewsItemDetails: React.FC<{ item: NewsItem }> = ({ item }) => (
  <BaseItemDetails
    title={item.title || "Untitled news item"}
    externalId={item.external_id}
  >
    <p className="text-gray-500 text-[11px] leading-4 truncate">
      {formatAuthor(item.author)}
    </p>
    {item.date && (
      <p className="text-gray-500 text-[11px] leading-4 truncate">
        {item.date}
      </p>
    )}
  </BaseItemDetails>
);

const LegalCaseItemDetails: React.FC<{ item: LegalCaseItem }> = ({ item }) => (
  <BaseItemDetails
    title={item.title || "Untitled legal case"}
    externalId={item.external_id}
  >
    <p className="text-gray-500 text-[11px] leading-4 truncate">
      {formatDateRange(item.start_date, item.end_date)}
    </p>
  </BaseItemDetails>
);

const DiligenceItemDetails: React.FC<{ item: DiligenceItem }> = ({ item }) => (
  <BaseItemDetails
    title={item.name || "Untitled diligence item"}
    externalId={item.external_id}
  >
    <p className="text-gray-500 text-[11px] leading-4 truncate">
      {item.source}
    </p>
    {item.date && (
      <p className="text-gray-500 text-[11px] leading-4 truncate">
        {item.date}
      </p>
    )}
  </BaseItemDetails>
);

const CopyFromReportModal: React.FC<CopyFromReportModalProps> = ({
  open,
  isSummaryStep,
  showNewsSection,
  showLegalSection,
  showDiligenceSection,
  sourceReportUuid,
  sourceNewsItems,
  sourceLegalItems,
  sourceDiligenceItems,
  selectedNewsItems,
  selectedLegalItems,
  selectedDiligenceItems,
  selectedNewsIds,
  selectedLegalIds,
  selectedDiligenceIds,
  loadCopySourceError,
  isLoadingCopySource,
  isCopyingItems,
  destinationReportUuid,
  onClose,
  onSourceReportUuidChange,
  onLoadSource,
  onToggleNews,
  onToggleLegal,
  onToggleDiligence,
  onSelectAllNews,
  onUnselectAllNews,
  onSelectAllLegal,
  onUnselectAllLegal,
  onSelectAllDiligence,
  onUnselectAllDiligence,
  onProceedToSummary,
  onBackToSelection,
  onClearInformation,
  onCopyInProgressChange,
  onCopySuccess,
  onCopyError,
}) => {
  const queryClient = useQueryClient();
  const { mutateAsync: cloneReportDocuments, isLoading: isCloneLoading } =
    useCloneReportDocuments();
  const [isCopyFlowPending, setIsCopyFlowPending] = useState(false);

  useEffect(() => {
    onCopyInProgressChange(isCloneLoading || isCopyFlowPending);
  }, [isCloneLoading, isCopyFlowPending, onCopyInProgressChange]);

  const handleConfirmCopy = async () => {
    const destId = destinationReportUuid.trim();
    const srcId = sourceReportUuid.trim();
    if (srcId && destId && srcId.toLowerCase() === destId.toLowerCase()) {
      onCopyError(
        "The source report cannot be the same as the report you are editing. Enter a different report ID."
      );
      return;
    }
    if (!destId) {
      onCopyError("Destination report is not available. Try again.");
      return;
    }
    const newsIdsToCopy = showNewsSection ? selectedNewsIds : [];
    const legalIdsToCopy = showLegalSection ? selectedLegalIds : [];
    const diligenceIdsToCopy = showDiligenceSection ? selectedDiligenceIds : [];
    const hasNewsToCopy = newsIdsToCopy.length > 0;
    const hasLegalToCopy = legalIdsToCopy.length > 0;
    const hasDiligenceToCopy = diligenceIdsToCopy.length > 0;
    if (!hasNewsToCopy && !hasLegalToCopy && !hasDiligenceToCopy) {
      onCopyError("Select at least one item to copy.");
      return;
    }
    setIsCopyFlowPending(true);
    try {
      await cloneReportDocuments({
        reportUuid: destId,
        body: {
          news_item_uuids: newsIdsToCopy,
          legal_case_item_uuids: legalIdsToCopy,
          diligence_item_uuids: diligenceIdsToCopy,
        },
      });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [`itemdal/reports/${destId}/news`],
        }),
        queryClient.invalidateQueries({
          queryKey: [`itemdal/reports/${destId}/legal_cases`],
        }),
        queryClient.invalidateQueries({
          queryKey: [`itemdal/reports/${destId}/diligence`],
        }),
        queryClient.invalidateQueries({ queryKey: ["itemDocuments"] }),
      ]);
      onCopySuccess();
    } catch {
      onCopyError("Failed to copy selected items. Please try again.");
    } finally {
      setIsCopyFlowPending(false);
    }
  };

  const previewNews = selectedNewsItems.slice(0, 5);
  const previewLegal = selectedLegalItems.slice(0, 5);
  const previewDiligence = selectedDiligenceItems.slice(0, 5);
  const remainingNews = selectedNewsItems.length - previewNews.length;
  const remainingLegal = selectedLegalItems.length - previewLegal.length;
  const remainingDiligence =
    selectedDiligenceItems.length - previewDiligence.length;
  const hasItemsToCopy =
    (showNewsSection && selectedNewsIds.length > 0) ||
    (showLegalSection && selectedLegalIds.length > 0) ||
    (showDiligenceSection && selectedDiligenceIds.length > 0);

  const visibleSectionCount = [
    showNewsSection,
    showLegalSection,
    showDiligenceSection,
  ].filter(Boolean).length;
  const statGridCols = [
    "grid-cols-2",
    "grid-cols-2",
    "grid-cols-3",
    "grid-cols-4",
  ] as const;
  const previewGridCols = [
    "grid-cols-1",
    "grid-cols-1",
    "grid-cols-2",
    "grid-cols-3",
  ] as const;
  const summaryStatGridClass = statGridCols[visibleSectionCount];
  const previewGridClass = previewGridCols[visibleSectionCount];

  const copyLoadErrorBanner = loadCopySourceError ? (
    <div
      id="copy-from-report-load-error"
      role="alert"
      aria-live="assertive"
      className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
    >
      {loadCopySourceError}
    </div>
  ) : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isSummaryStep ? "Summary" : "Copy Items From Report"}
      width="w-full max-w-6xl"
    >
      <div className="mt-4 space-y-4">
        {!isSummaryStep ? (
          <>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <label
                  htmlFor="copy-from-report-id"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Report ID
                </label>
                <input
                  id="copy-from-report-id"
                  type="text"
                  aria-invalid={!!loadCopySourceError}
                  aria-describedby={
                    loadCopySourceError
                      ? "copy-from-report-load-error"
                      : undefined
                  }
                  className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                    loadCopySourceError
                      ? "border-red-500 bg-red-50/40 focus:ring-red-500"
                      : "border-gray-300 focus:ring-ourGreen"
                  }`}
                  placeholder="Enter report ID (UUID)"
                  value={sourceReportUuid}
                  onChange={(event) =>
                    onSourceReportUuidChange(event.target.value)
                  }
                  disabled={isLoadingCopySource || isCopyingItems}
                />
              </div>
              <button
                type="button"
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  isLoadingCopySource || isCopyingItems
                    ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                    : "bg-ourGreen text-white hover:bg-green-700"
                }`}
                onClick={onLoadSource}
                disabled={isLoadingCopySource || isCopyingItems}
              >
                {isLoadingCopySource ? "Applying..." : "Apply"}
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
                onClick={onClearInformation}
                disabled={isCopyingItems || isLoadingCopySource}
              >
                Clear Information
              </button>
            </div>
            {copyLoadErrorBanner}
            <div className="h-[26rem] flex gap-4">
              {showNewsSection && (
                <div className="bg-white rounded-lg border border-gray-300 shadow-sm overflow-auto flex-1 min-w-0">
                  <div className="sticky top-0 z-20 bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <div className="flex items-center justify-between gap-3">
                      <h4 className="text-sm font-semibold text-gray-700">
                        News and Web ({sourceNewsItems.length})
                      </h4>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="px-2.5 py-1 rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 text-xs disabled:cursor-not-allowed disabled:text-gray-400"
                          onClick={onSelectAllNews}
                          disabled={isCopyingItems || !sourceNewsItems.length}
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          className="px-2.5 py-1 rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 text-xs disabled:cursor-not-allowed disabled:text-gray-400"
                          onClick={onUnselectAllNews}
                          disabled={isCopyingItems || !sourceNewsItems.length}
                        >
                          Unselect all
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="py-2 flex flex-col gap-2">
                    {sourceNewsItems.length ? (
                      sourceNewsItems.map((item) => (
                        <label
                          key={item.external_id}
                          className="flex flex-row items-start gap-2 w-full px-2.5 pb-2 border-b border-gray-200 last:border-b-0 last:pb-0 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-ourGreen focus:ring-ourGreen"
                            checked={selectedNewsIds.includes(item.external_id)}
                            onChange={() => onToggleNews(item.external_id)}
                            disabled={isCopyingItems}
                          />
                          <NewsItemDetails item={item} />
                        </label>
                      ))
                    ) : (
                      <div className="text-gray-500 text-xs py-1 px-3">
                        No news items loaded.
                      </div>
                    )}
                  </div>
                </div>
              )}
              {showLegalSection && (
                <div className="bg-white rounded-lg border border-gray-300 shadow-sm overflow-auto flex-1 min-w-0">
                  <div className="sticky top-0 z-20 bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <div className="flex items-center justify-between gap-3">
                      <h4 className="text-sm font-semibold text-gray-700">
                        Legal Cases ({sourceLegalItems.length})
                      </h4>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="px-2.5 py-1 rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 text-xs disabled:cursor-not-allowed disabled:text-gray-400"
                          onClick={onSelectAllLegal}
                          disabled={isCopyingItems || !sourceLegalItems.length}
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          className="px-2.5 py-1 rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 text-xs disabled:cursor-not-allowed disabled:text-gray-400"
                          onClick={onUnselectAllLegal}
                          disabled={isCopyingItems || !sourceLegalItems.length}
                        >
                          Unselect all
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="py-2 flex flex-col gap-2">
                    {sourceLegalItems.length ? (
                      sourceLegalItems.map((item) => (
                        <label
                          key={item.external_id}
                          className="flex flex-row items-start gap-2 w-full px-2.5 pb-2 border-b border-gray-200 last:border-b-0 last:pb-0 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-ourGreen focus:ring-ourGreen"
                            checked={selectedLegalIds.includes(
                              item.external_id
                            )}
                            onChange={() => onToggleLegal(item.external_id)}
                            disabled={isCopyingItems}
                          />
                          <LegalCaseItemDetails item={item} />
                        </label>
                      ))
                    ) : (
                      <div className="text-gray-500 text-xs py-1 px-3">
                        No legal case items loaded.
                      </div>
                    )}
                  </div>
                </div>
              )}
              {showDiligenceSection && (
                <div className="bg-white rounded-lg border border-gray-300 shadow-sm overflow-auto flex-1 min-w-0">
                  <div className="sticky top-0 z-20 bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <div className="flex items-center justify-between gap-3">
                      <h4 className="text-sm font-semibold text-gray-700">
                        Diligence ({sourceDiligenceItems.length})
                      </h4>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="px-2.5 py-1 rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 text-xs disabled:cursor-not-allowed disabled:text-gray-400"
                          onClick={onSelectAllDiligence}
                          disabled={
                            isCopyingItems || !sourceDiligenceItems.length
                          }
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          className="px-2.5 py-1 rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 text-xs disabled:cursor-not-allowed disabled:text-gray-400"
                          onClick={onUnselectAllDiligence}
                          disabled={
                            isCopyingItems || !sourceDiligenceItems.length
                          }
                        >
                          Unselect all
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="py-2 flex flex-col gap-2">
                    {sourceDiligenceItems.length ? (
                      sourceDiligenceItems.map((item) => (
                        <label
                          key={item.external_id}
                          className="flex flex-row items-start gap-2 w-full px-2.5 pb-2 border-b border-gray-200 last:border-b-0 last:pb-0 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-ourGreen focus:ring-ourGreen"
                            checked={selectedDiligenceIds.includes(
                              item.external_id
                            )}
                            onChange={() => onToggleDiligence(item.external_id)}
                            disabled={isCopyingItems}
                          />
                          <DiligenceItemDetails item={item} />
                        </label>
                      ))
                    ) : (
                      <div className="text-gray-500 text-xs py-1 px-3">
                        No diligence items loaded.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="pt-2 flex justify-end gap-2 border-t border-gray-200">
              <button
                type="button"
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed"
                onClick={onClose}
                disabled={isCopyingItems || isLoadingCopySource}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  isCopyingItems || isLoadingCopySource || !hasItemsToCopy
                    ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                    : "bg-ourGreen text-white hover:bg-green-700"
                }`}
                onClick={onProceedToSummary}
                disabled={
                  isCopyingItems || isLoadingCopySource || !hasItemsToCopy
                }
              >
                Copy to Report
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="text-xs font-semibold text-gray-800">
                Review copy summary
              </p>
              <p className="text-[11px] text-gray-600 mt-0.5">
                Confirm these selections before copying into the current report.
              </p>
              <div className="mt-2 rounded-md border border-gray-200 bg-white px-2.5 py-2">
                <p className="text-[11px] uppercase tracking-wide text-gray-500">
                  Source report ID
                </p>
                <p className="text-xs font-medium text-gray-700 break-all">
                  {sourceReportUuid || "N/A"}
                </p>
              </div>
            </div>
            <div className={`grid ${summaryStatGridClass} gap-2`}>
              {showNewsSection && (
                <div className="rounded-lg border border-gray-200 p-2.5 bg-white">
                  <p className="text-xs text-gray-500">News and Web</p>
                  <p className="text-lg font-semibold text-gray-800 leading-6">
                    {selectedNewsItems.length}
                  </p>
                  <p className="text-[11px] text-gray-500">
                    of {sourceNewsItems.length} loaded
                  </p>
                </div>
              )}
              {showLegalSection && (
                <div className="rounded-lg border border-gray-200 p-2.5 bg-white">
                  <p className="text-xs text-gray-500">Legal Cases</p>
                  <p className="text-lg font-semibold text-gray-800 leading-6">
                    {selectedLegalItems.length}
                  </p>
                  <p className="text-[11px] text-gray-500">
                    of {sourceLegalItems.length} loaded
                  </p>
                </div>
              )}
              {showDiligenceSection && (
                <div className="rounded-lg border border-gray-200 p-2.5 bg-white">
                  <p className="text-xs text-gray-500">Diligence</p>
                  <p className="text-lg font-semibold text-gray-800 leading-6">
                    {selectedDiligenceItems.length}
                  </p>
                  <p className="text-[11px] text-gray-500">
                    of {sourceDiligenceItems.length} loaded
                  </p>
                </div>
              )}
              <div className="rounded-lg border border-green-200 bg-green-50 p-2.5">
                <p className="text-xs text-green-700">Total items to copy</p>
                <p className="text-lg font-semibold text-green-800 leading-6">
                  {selectedNewsItems.length +
                    selectedLegalItems.length +
                    selectedDiligenceItems.length}
                </p>
                <p className="text-[11px] text-green-700">selected items</p>
              </div>
            </div>
            <div className={`grid ${previewGridClass} gap-3 min-h-[14rem]`}>
              {showNewsSection && (
                <div className="rounded-lg border border-gray-300 shadow-sm overflow-auto">
                  <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 text-xs font-semibold text-gray-700">
                    News and Web Preview
                  </div>
                  <div className="p-2 space-y-1">
                    {previewNews.length ? (
                      previewNews.map((item) => (
                        <NewsItemDetails item={item} key={item.external_id} />
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">
                        No news items selected.
                      </p>
                    )}
                    {remainingNews > 0 && (
                      <p className="text-xs text-gray-500">
                        + {remainingNews} more news item(s)
                      </p>
                    )}
                  </div>
                </div>
              )}
              {showLegalSection && (
                <div className="rounded-lg border border-gray-300 shadow-sm overflow-auto">
                  <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 text-xs font-semibold text-gray-700">
                    Legal Cases Preview
                  </div>
                  <div className="p-2 space-y-1">
                    {previewLegal.length ? (
                      previewLegal.map((item) => (
                        <LegalCaseItemDetails
                          item={item}
                          key={item.external_id}
                        />
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">
                        No legal case items selected.
                      </p>
                    )}
                    {remainingLegal > 0 && (
                      <p className="text-xs text-gray-500">
                        + {remainingLegal} more legal case(s)
                      </p>
                    )}
                  </div>
                </div>
              )}
              {showDiligenceSection && (
                <div className="rounded-lg border border-gray-300 shadow-sm overflow-auto">
                  <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 text-xs font-semibold text-gray-700">
                    Diligence Preview
                  </div>
                  <div className="p-2 space-y-1">
                    {previewDiligence.length ? (
                      previewDiligence.map((item) => (
                        <DiligenceItemDetails
                          item={item}
                          key={item.external_id}
                        />
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">
                        No diligence items selected.
                      </p>
                    )}
                    {remainingDiligence > 0 && (
                      <p className="text-xs text-gray-500">
                        + {remainingDiligence} more diligence item(s)
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
            {copyLoadErrorBanner}
            <div className="pt-2 flex justify-end gap-2 border-t border-gray-200">
              <button
                type="button"
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed"
                onClick={onBackToSelection}
                disabled={isCopyingItems}
              >
                Back
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed"
                onClick={onClose}
                disabled={isCopyingItems}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  isCopyingItems ||
                  !hasItemsToCopy ||
                  !destinationReportUuid.trim()
                    ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                    : "bg-ourGreen text-white hover:bg-green-700"
                }`}
                onClick={handleConfirmCopy}
                disabled={
                  isCopyingItems ||
                  !hasItemsToCopy ||
                  !destinationReportUuid.trim()
                }
              >
                {isCopyingItems ? "Submitting..." : "Submit"}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default CopyFromReportModal;
